import { Link } from 'react-router-dom';
import { Volume2, VolumeX, FileJson, FileSpreadsheet, Sun, Moon, Monitor, Wrench } from 'lucide-react';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { isMuted, setMuted } from '../lib/sound';
import { downloadExportJson, downloadExportCsv, seedReal2026, repairAzioniSettimana } from '../lib/api';
import { useTheme } from '../lib/useTheme';
import Breadcrumb from './nav/Breadcrumb';
import AreaColorPicker from './nav/AreaColorPicker';
import AreaManager from './nav/AreaManager';
import CalendarConnect from './nav/CalendarConnect';
import ConfirmDialog from './nav/ConfirmDialog';

/**
 * Pagina Legenda + Impostazioni minime.
 * - Tabella sigla → esteso inglese → descrizione italiana
 * - Toggle suono di completamento
 */
const ENTRIES = [
  {
    sigla: 'MIT',
    esteso: 'Most Important Task',
    descrizione: 'Le 1-3 cose più importanti del giorno. Si spuntano quando fatte; quelle non spuntate slittano alla pianificazione successiva.',
  },
  {
    sigla: 'KR',
    esteso: 'Key Result',
    descrizione: 'Risultato misurabile che dimostra il progresso su un obiettivo. Può essere numerico (es. fatturato), una tappa (sì/no) o un\'abitudine (frequenza).',
  },
  {
    sigla: 'KPI',
    esteso: 'Key Performance Indicator',
    descrizione: 'Indicatore di performance ricorrente, di solito non legato a un singolo obiettivo. Misura uno stato, non un cambiamento.',
  },
  {
    sigla: 'BAU',
    esteso: 'Business As Usual',
    descrizione: 'Lavoro corrente, non legato a un Key Result specifico. Lo si traccia per non sottovalutare la capacità realmente disponibile.',
  },
  {
    sigla: 'OKR',
    esteso: 'Objectives and Key Results',
    descrizione: 'Sistema di pianificazione per obiettivi: un Obiettivo qualitativo abbinato a 1-5 Key Result misurabili. È il framework alla base di Quantum Planner.',
  },
];

export default function PaginaLegenda() {
  const [muted, setMutedState] = useState(false);
  const [exporting, setExporting] = useState(null); // 'json' | 'csv' | null
  const [seedDialogOpen, setSeedDialogOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const { theme, setTheme } = useTheme();

  const handleSeedReal = async () => {
    setSeedDialogOpen(false);
    setSeeding(true);
    try {
      await seedReal2026();
      toast.success('Stato reale 2026 ricaricato');
    } catch (e) {
      toast.error('Errore ricaricamento stato reale');
    } finally {
      setSeeding(false);
    }
  };

  const handleRepairAzioni = async () => {
    setRepairing(true);
    try {
      const r = await repairAzioniSettimana();
      if (r.fixed > 0) {
        toast.success(`Riparate ${r.fixed} azioni su ${r.scanned} scansionate`);
      } else {
        toast.success(`Nessuna azione da riparare (${r.scanned} scansionate)`);
      }
    } catch (e) {
      toast.error('Errore durante la riparazione');
    } finally {
      setRepairing(false);
    }
  };

  useEffect(() => {
    setMutedState(isMuted());
    const onChange = (e) => setMutedState(!!e.detail);
    window.addEventListener('qp:sound-muted-change', onChange);
    return () => window.removeEventListener('qp:sound-muted-change', onChange);
  }, []);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setMutedState(next);
  };

  const triggerDownload = (blob, fallbackName, headers) => {
    let filename = fallbackName;
    const cd = headers?.['content-disposition'] || headers?.['Content-Disposition'];
    if (cd) {
      const m = /filename="?([^"]+)"?/i.exec(cd);
      if (m) filename = m[1];
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportJson = async () => {
    setExporting('json');
    try {
      const { blob, headers } = await downloadExportJson();
      const stamp = new Date().toISOString().slice(0, 10);
      triggerDownload(blob, `quantumplanner-export-${stamp}.json`, headers);
      toast.success('Export JSON scaricato');
    } catch (e) {
      toast.error(`Export fallito · ${e?.response?.data?.detail || e?.message || 'errore'}`);
    } finally {
      setExporting(null);
    }
  };

  const handleExportCsv = async () => {
    setExporting('csv');
    try {
      const { blob, headers } = await downloadExportCsv();
      const stamp = new Date().toISOString().slice(0, 10);
      triggerDownload(blob, `quantumplanner-export-${stamp}.zip`, headers);
      toast.success('Export CSV scaricato (ZIP)');
    } catch (e) {
      toast.error(`Export fallito · ${e?.response?.data?.detail || e?.message || 'errore'}`);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <div className="max-w-7xl mx-auto px-6 pt-2">
        <Breadcrumb
          backTo="/"
          backLabel="Dashboard"
          items={[{ label: 'Dashboard', to: '/' }, { label: 'Legenda · Impostazioni' }]}
        />
      </div>

      <div className="max-w-3xl mx-auto px-6 pb-20">
        <header className="border-b border-line pb-8 mb-12" data-testid="legenda-header">
          <span className="mono-label">Sigle · impostazioni</span>
          <h1 className="font-display tracking-tight text-4xl md:text-5xl lg:text-6xl mt-2">Legenda</h1>
          <p className="font-body text-base text-soft mt-4 leading-relaxed max-w-2xl">
            Quantum Planner usa le sigle standard del lessico OKR. Qui trovi forma estesa
            e significato. Forma estesa in inglese (originale), spiegazione in italiano.
          </p>
        </header>

        <table className="w-full mb-16" data-testid="legenda-table">
          <thead>
            <tr className="border-b border-ink">
              <th className="text-left py-3 font-mono text-xs uppercase tracking-[0.18em] text-soft w-[80px]">Sigla</th>
              <th className="text-left py-3 font-mono text-xs uppercase tracking-[0.18em] text-soft w-[200px]">Esteso (EN)</th>
              <th className="text-left py-3 font-mono text-xs uppercase tracking-[0.18em] text-soft">Cosa significa</th>
            </tr>
          </thead>
          <tbody>
            {ENTRIES.map((e) => (
              <tr key={e.sigla} className="border-b border-linesoft" data-testid={`legenda-row-${e.sigla}`}>
                <td className="py-4 align-top">
                  <span className="font-mono text-base font-bold tracking-tight">{e.sigla}</span>
                </td>
                <td className="py-4 align-top">
                  <span className="font-display text-base">{e.esteso}</span>
                </td>
                <td className="py-4 align-top font-body text-sm text-soft leading-relaxed">
                  {e.descrizione}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Impostazioni */}
        <section data-testid="impostazioni-section">
          <h2 className="font-display text-2xl tracking-tight mb-6 border-b border-line pb-3">Impostazioni</h2>

          {/* Tema */}
          <div className="flex items-start justify-between py-4 border-b border-linesoft" data-testid="theme-row">
            <div className="flex-1">
              <div className="font-display text-base mb-1">Tema</div>
              <p className="font-body text-sm text-soft leading-relaxed max-w-md">
                Palette chiara (crema) o scura (ink). L&apos;accento rosso resta invariato in entrambi.
                Al primo avvio rispetta la preferenza di sistema; poi viene ricordata la tua scelta.
              </p>
            </div>
            <div
              className="inline-flex items-center border border-line divide-x divide-line"
              data-testid="theme-toggle-group"
              role="radiogroup"
              aria-label="Tema interfaccia"
            >
              {[
                { v: 'system', label: 'Sistema', Icon: Monitor },
                { v: 'light', label: 'Chiaro', Icon: Sun },
                { v: 'dark', label: 'Scuro', Icon: Moon },
              ].map(({ v, label, Icon }) => {
                const active = theme === v;
                return (
                  <button
                    key={v}
                    onClick={() => setTheme(v)}
                    className={
                      'inline-flex items-center gap-2 px-3 py-2 font-mono text-xs uppercase tracking-[0.18em] transition-colors ' +
                      (active
                        ? 'bg-ink text-canvas'
                        : 'text-soft hover:text-ink hover:bg-linesoft')
                    }
                    data-testid={`theme-toggle-${v}`}
                    aria-pressed={active}
                    role="radio"
                    aria-checked={active}
                  >
                    <Icon size={14} strokeWidth={1.5} />
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <CalendarConnect />

          <div className="flex items-start justify-between py-4 border-b border-linesoft">
            <div className="flex-1">
              <div className="font-display text-base mb-1">Suono di completamento</div>
              <p className="font-body text-sm text-soft leading-relaxed max-w-md">
                Un breve campanellino quando spunti una Most Important Task (MIT), un&apos;azione, una tappa.
                Nessun suono quando despunti.
              </p>
            </div>
            <button
              onClick={toggleMute}
              className={
                'inline-flex items-center gap-2 px-4 py-2 border font-mono text-xs uppercase tracking-[0.18em] transition-colors ' +
                (muted
                  ? 'border-line text-mute hover:border-ink hover:text-ink'
                  : 'border-ink bg-ink text-canvas')
              }
              data-testid="sound-toggle-btn"
            >
              {muted
                ? <><VolumeX size={14} strokeWidth={1.5} /> Disattivato</>
                : <><Volume2 size={14} strokeWidth={1.5} /> Attivo</>
              }
            </button>
          </div>

          {/* Colori aree (T4) */}
          <AreaColorPicker />

          {/* Gestione aree: rinomina, colori, nuove aree, riordino (prima era in Dashboard) */}
          <div className="py-4 border-b border-linesoft" data-testid="area-manager-section">
            <AreaManager />
          </div>


          <div className="py-4 border-b border-linesoft" data-testid="export-section">
            <div className="mb-3">
              <div className="font-display text-base mb-1">Esporta i tuoi dati</div>
              <p className="font-body text-sm text-soft leading-relaxed max-w-md">
                Backup e portabilità. JSON contiene tutto (anni, obiettivi, KR, target,
                azioni, MIT). CSV è un archivio ZIP con un file per tabella, pronto per
                Excel/Numbers. Nessun lock-in: i dati restano tuoi.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleExportJson}
                disabled={exporting !== null}
                className="inline-flex items-center gap-2 px-4 py-2 border border-ink text-ink hover:bg-ink hover:text-canvas transition-colors font-mono text-xs uppercase tracking-[0.18em] disabled:opacity-50 disabled:cursor-wait"
                data-testid="export-json-btn"
              >
                <FileJson size={14} strokeWidth={1.5} />
                {exporting === 'json' ? 'Esporto…' : 'Scarica JSON'}
              </button>
              <button
                onClick={handleExportCsv}
                disabled={exporting !== null}
                className="inline-flex items-center gap-2 px-4 py-2 border border-line text-soft hover:border-ink hover:text-ink transition-colors font-mono text-xs uppercase tracking-[0.18em] disabled:opacity-50 disabled:cursor-wait"
                data-testid="export-csv-btn"
              >
                <FileSpreadsheet size={14} strokeWidth={1.5} />
                {exporting === 'csv' ? 'Esporto…' : 'Scarica CSV (ZIP)'}
              </button>
            </div>
          </div>
          {/* Manutenzione dati — operazioni sicure di ripulitura */}
          <div className="py-4 border-b border-linesoft" data-testid="maintenance-section">
            <div className="mb-3">
              <div className="font-display text-base mb-1">Manutenzione dati</div>
              <p className="font-body text-sm text-soft leading-relaxed max-w-md">
                Riallinea il campo «settimana» all&apos;ISO week della «data» per le
                azioni in cui i due campi risultano incoerenti (record legacy
                creati prima del write-side sync). Operazione idempotente e
                sicura: puoi rieseguirla senza rischi.
              </p>
            </div>
            <button
              onClick={handleRepairAzioni}
              disabled={repairing}
              className="inline-flex items-center gap-2 px-4 py-2 border border-line text-soft hover:border-ink hover:text-ink transition-colors font-mono text-xs uppercase tracking-[0.18em] disabled:opacity-50 disabled:cursor-wait"
              data-testid="repair-azioni-settimana-btn"
            >
              <Wrench size={14} strokeWidth={1.5} />
              {repairing ? 'Riparo…' : 'Ripara azioni · settimana↔data'}
            </button>
          </div>

          {/* Zona dati — azioni distruttive, tenute fuori dalla Dashboard */}
          <div className="py-4" data-testid="danger-zone-section">
            <div className="mb-3">
              <div className="font-display text-base mb-1 text-risk">Zona dati</div>
              <p className="font-body text-sm text-soft leading-relaxed max-w-md">
                «Ricarica stato reale 2026» sovrascrive TUTTI i dati correnti con il seed
                di partenza. Irreversibile: prima di procedere scarica un export.
              </p>
            </div>
            <button
              onClick={() => setSeedDialogOpen(true)}
              disabled={seeding}
              className="inline-flex items-center gap-2 px-4 py-2 border border-risk/50 text-risk hover:bg-risk hover:text-white transition-colors font-mono text-xs uppercase tracking-[0.18em] disabled:opacity-50 disabled:cursor-wait"
              data-testid="reload-real-2026-btn"
            >
              {seeding ? 'Ricarico…' : 'Ricarica stato reale 2026'}
            </button>
          </div>
        </section>
      </div>

      <ConfirmDialog
        open={seedDialogOpen}
        danger
        title="Ricaricare lo stato reale 2026?"
        desc="Tutti i dati attuali (obiettivi, KR, azioni, MIT) verranno sovrascritti con il seed. L'operazione non si può annullare."
        typeToConfirm="2026"
        confirmLabel="Sovrascrivi"
        onConfirm={handleSeedReal}
        onCancel={() => setSeedDialogOpen(false)}
        testid="seed-confirm-dialog"
      />
    </div>
  );
}
